import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { usePartnersAuth } from '@partners/auth/PartnersAuth';
import { Notice, Section } from '@partners/components/Shell';
import { SignaturePad, type SignaturePadHandle } from '@partners/components/SignaturePad';
import { promoterErrorKey, terms, termsSign, type TermsDocument } from '@partners/data/promoter';
import { base64ToBytes, sha256Hex } from '@partners/lib/bytes';
import { pdfObjectUrl, revokeObjectUrl } from '@partners/lib/pdfUrl';

interface Loaded {
  doc: TermsDocument;
  /** SHA-256 of the bytes THIS device decoded and shows: what is sent with the signature. */
  clientSha: string;
  url: string | null;
}

/**
 * The agreement gate (design §9.3 screen 5, ESIGN). The PDF comes from promoter_terms() as bytes; this device
 * hashes them with WebCrypto, shows the hash, renders the same bytes, and signs with that hash: the server
 * refuses a signature whose client hash is not the stored document's. The consent text and its version come
 * from the server in the promoter's language; the box must be checked and the pad drawn before anything is sent.
 */
export function Agreement({ onSigned }: { onSigned: () => void }) {
  const { t } = useTranslation();
  const { client } = usePartnersAuth();
  const [loaded, setLoaded] = React.useState<Loaded | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [consent, setConsent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [generation, setGeneration] = React.useState(0);
  const pad = React.useRef<SignaturePadHandle>(null);

  React.useEffect(() => {
    let live = true;
    let url: string | null = null;
    setLoaded(null);
    setLoadError(null);
    void (async () => {
      try {
        const doc = await terms(client);
        const bytes = base64ToBytes(doc.pdf_base64);
        const clientSha = await sha256Hex(bytes);
        url = pdfObjectUrl(bytes);
        if (live) setLoaded({ doc, clientSha, url });
        else revokeObjectUrl(url);
      } catch (err) {
        console.warn('promoter_terms failed', err);
        if (live) setLoadError(t(`promoter.errors.${promoterErrorKey(err)}`));
      }
    })();
    return () => {
      live = false;
      revokeObjectUrl(url);
    };
  }, [client, t, generation]);

  const reload = () => {
    setError(null);
    setConsent(false);
    pad.current?.clear();
    setGeneration((g) => g + 1);
  };

  const mismatch = loaded !== null && loaded.clientSha !== loaded.doc.sha256.toLowerCase();

  async function onSign() {
    if (!loaded?.doc.consent) return;
    if (!consent) return setError(t('promoter.errors.consentRequired'));
    const png = pad.current?.toPng() ?? null;
    if (!png) return setError(t('promoter.errors.signatureRequired'));
    setBusy(true);
    setError(null);
    try {
      await termsSign(client, {
        documentId: loaded.doc.document_id,
        documentSha256: loaded.clientSha,
        consentVersion: loaded.doc.consent.version,
        consentLocale: loaded.doc.consent.locale,
        signaturePng: png,
      });
      onSigned();
    } catch (err) {
      console.warn('promoter_terms_sign refused', err);
      setError(t(`promoter.errors.${promoterErrorKey(err)}`));
      setBusy(false);
    }
  }

  const reloadButton = (
    <Button variant="outline" onClick={reload}>
      {t('promoter.agreement.reload')}
    </Button>
  );

  return (
    <>
      <div>
        <h1 className="text-2xl font-semibold text-ink">{t('promoter.agreement.title')}</h1>
        <p className="mt-1 text-base text-ink-secondary">{t('promoter.agreement.body')}</p>
      </div>

      {loadError && (
        <>
          <Notice>{loadError}</Notice>
          {reloadButton}
        </>
      )}
      {!loaded && !loadError && (
        <p className="text-ink-muted" role="status">
          {t('promoter.common.loading')}
        </p>
      )}

      {loaded && (
        <>
          <Section
            id="agreement-pdf"
            title={loaded.doc.program_label}
            action={
              loaded.url ? (
                <Button asChild variant="outline" size="sm">
                  <a href={loaded.url} target="_blank" rel="noopener">
                    {t('promoter.common.openPdf')}
                  </a>
                </Button>
              ) : undefined
            }
          >
            <dl className="mb-4 grid gap-2 text-sm">
              <div className="flex flex-wrap gap-x-2">
                <dt className="text-ink-muted">{t('promoter.agreement.version')}</dt>
                <dd className="text-ink">{loaded.doc.version}</dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-ink-muted">{t('promoter.agreement.fingerprint')}</dt>
                <dd className="break-all font-mono text-xs text-ink">{loaded.clientSha}</dd>
              </div>
            </dl>
            {mismatch && <Notice className="mb-4">{t('promoter.agreement.fingerprintMismatch')}</Notice>}
            {loaded.url ? (
              <iframe
                title={t('promoter.agreement.pdfTitle')}
                src={loaded.url}
                className="h-[60vh] min-h-80 w-full rounded-md border border-hairline bg-sunken"
              />
            ) : (
              <p className="text-sm text-ink-muted">{t('promoter.agreement.pdfFallback')}</p>
            )}
          </Section>

          <Section id="agreement-consent" title={t('promoter.agreement.consentTitle')}>
            {loaded.doc.consent && (
              <>
                <p className="whitespace-pre-line text-base text-ink-secondary">{loaded.doc.consent.body}</p>
                <p className="mt-2 text-sm text-ink-muted">{t('promoter.agreement.consentVersion', { version: loaded.doc.consent.version })}</p>
                <label className="mt-4 flex cursor-pointer items-start gap-3 text-base text-ink">
                  <Checkbox checked={consent} onCheckedChange={(v) => setConsent(v === true)} className="mt-0.5" />
                  <span>{t('promoter.agreement.consentCheckbox')}</span>
                </label>
              </>
            )}
          </Section>

          <Section id="agreement-signature" title={t('promoter.agreement.signatureTitle')}>
            <SignaturePad
              ref={pad}
              label={t('promoter.agreement.signatureLabel')}
              clearLabel={t('promoter.agreement.clear')}
              hint={t('promoter.agreement.signatureHint')}
            />
          </Section>

          <Notice>{error}</Notice>
          {error === t('promoter.errors.agreementChanged') || error === t('promoter.errors.consentChanged') ? reloadButton : null}
          <Button onClick={() => void onSign()} disabled={busy || mismatch || !loaded.doc.consent}>
            {busy ? t('promoter.agreement.signing') : t('promoter.agreement.sign')}
          </Button>
        </>
      )}
    </>
  );
}
